import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { LanguageSwitcher } from "../components/LanguageSwitcher";
import { useLocale } from "../lib/i18n";
import { api } from "../lib/api";

interface ProviderProfile {
  id: string;
  headline?: string | null;
  bio?: string | null;
  city?: string | null;
  verificationLevel: "unverified" | "identity_verified" | "pro_verified" | "trusted_pro";
  ratingAvg: string;
  ratingCount: number;
  user: {
    displayName?: string | null;
    firstName?: string | null;
    phone?: string | null;
    telegramUsername?: string | null;
    avatarUrl?: string | null;
  };
  categories: Array<{ slug: string; nameRo: string; nameRu?: string | null }>;
  portfolioCases: Array<{
    id: string;
    title: string;
    description: string;
    media: Array<{ id: string; fileUrl: string }>;
  }>;
}

interface Lightbox {
  images: string[];
  index: number;
}

export function ProviderDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { locale, t } = useLocale();
  const [profile, setProfile] = useState<ProviderProfile | null>(null);
  const [lightbox, setLightbox] = useState<Lightbox | null>(null);

  useEffect(() => {
    if (!id) return;
    api.get<ProviderProfile>(`/handymen/public/${id}`).then(({ data }) => setProfile(data));
  }, [id]);

  useEffect(() => {
    if (!lightbox) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") setLightbox(null);
      if (e.key === "ArrowRight") setLightbox((prev) => prev && prev.index < prev.images.length - 1 ? { ...prev, index: prev.index + 1 } : prev);
      if (e.key === "ArrowLeft") setLightbox((prev) => prev && prev.index > 0 ? { ...prev, index: prev.index - 1 } : prev);
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [lightbox]);

  const openLightbox = (images: string[], index: number) => {
    setLightbox({ images, index });
  };

  if (!profile) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50">
        <div className="text-sm text-gray-400">{t.loading}</div>
      </div>
    );
  }

  const name = profile.user.displayName || profile.user.firstName || "Handyman";
  const initials = name.split(" ").map((w) => w[0]).join("").slice(0, 2).toUpperCase();
  const telegramLink = profile.user.telegramUsername ? `https://t.me/${profile.user.telegramUsername}` : null;

  const verificationKey =
    profile.verificationLevel === "identity_verified" ? "identityVerified"
    : profile.verificationLevel === "pro_verified" ? "proVerified"
    : profile.verificationLevel === "trusted_pro" ? "trustedPro"
    : "unverified";

  const verificationColors: Record<string, string> = {
    unverified: "bg-gray-100 text-gray-500",
    identityVerified: "bg-orange-50 text-orange-600",
    proVerified: "bg-emerald-50 text-emerald-700",
    trustedPro: "bg-emerald-100 text-emerald-800",
  };

  const verificationIcons: Record<string, string> = {
    unverified: "",
    identityVerified: "✓",
    proVerified: "✓✓",
    trustedPro: "★",
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="border-b border-gray-100 bg-white px-6 py-5">
        <div className="mb-4 flex items-center justify-between">
          <button type="button" onClick={() => navigate(-1)} className="text-sm font-semibold text-gray-500">
            {t.back}
          </button>
          <LanguageSwitcher />
        </div>
        <div className="text-2xl font-bold text-gray-900">{t.providerProfileTitle}</div>
      </div>

      <div className="space-y-4 p-5">
        {/* Profile card */}
        <div className="rounded-2xl bg-white p-5 shadow-sm">
          <div className="flex gap-4">
            {/* Avatar */}
            <div className="shrink-0">
              {profile.user.avatarUrl ? (
                <img src={profile.user.avatarUrl} alt={name} className="h-16 w-16 rounded-2xl object-cover" />
              ) : (
                <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-orange-100 text-xl font-bold text-orange-600">
                  {initials}
                </div>
              )}
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-xl font-bold text-gray-900 leading-tight">{name}</div>
              {profile.headline ? (
                <div className="mt-0.5 text-sm text-gray-500 leading-snug">{profile.headline}</div>
              ) : null}
              {profile.city ? (
                <div className="mt-1 flex items-center gap-1 text-xs text-gray-400">
                  <span>📍</span>
                  <span>{profile.city}</span>
                </div>
              ) : null}
            </div>
          </div>

          {/* Stats row */}
          <div className="mt-4 flex gap-3">
            <div className="flex-1 rounded-xl bg-gray-50 px-3 py-2.5 text-center">
              <div className="text-lg font-bold text-gray-900">★ {Number(profile.ratingAvg).toFixed(1)}</div>
              <div className="mt-0.5 text-xs text-gray-400">{profile.ratingCount} {t.reviewsCount}</div>
            </div>
            <div className={`flex-1 rounded-xl px-3 py-2.5 text-center ${verificationColors[verificationKey]}`}>
              <div className="text-sm font-bold">
                {verificationIcons[verificationKey]} {t.verificationLabels[verificationKey as keyof typeof t.verificationLabels]}
              </div>
              <div className="mt-0.5 text-xs opacity-70">{t.verificationLevel}</div>
            </div>
          </div>

          {/* Bio */}
          {profile.bio ? (
            <div className="mt-4 text-sm leading-6 text-gray-600">{profile.bio}</div>
          ) : null}

          {/* Categories */}
          {profile.categories.length > 0 ? (
            <div className="mt-4">
              <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-gray-400">{t.providerCategories}</div>
              <div className="flex flex-wrap gap-2">
                {profile.categories.map((category) => (
                  <span key={category.slug} className="rounded-full bg-orange-50 px-3 py-1 text-xs font-semibold text-orange-600">
                    {locale === "ru" ? category.nameRu || category.nameRo : category.nameRo}
                  </span>
                ))}
              </div>
            </div>
          ) : null}

          {/* Actions */}
          <div className="mt-5 grid gap-2.5">
            {telegramLink ? (
              <a href={telegramLink} target="_blank" rel="noreferrer" className="flex items-center justify-center gap-2 rounded-2xl bg-orange-500 px-4 py-3.5 text-sm font-semibold text-white active:bg-orange-600 transition-colors">
                <span>✈</span> {t.openTelegram}
              </a>
            ) : null}
            {profile.user.phone ? (
              <a href={`tel:${profile.user.phone}`} className="flex items-center justify-center gap-2 rounded-2xl border border-gray-200 bg-white px-4 py-3.5 text-sm font-semibold text-gray-700 active:bg-gray-50 transition-colors">
                <span>📞</span> {t.callPhone}
              </a>
            ) : null}
          </div>
        </div>

        {/* Portfolio */}
        <div className="rounded-2xl bg-white p-5 shadow-sm">
          <div className="mb-4 text-lg font-bold text-gray-900">{t.portfolioTitle}</div>
          {profile.portfolioCases.length === 0 ? (
            <div className="rounded-xl bg-gray-50 px-4 py-6 text-center text-sm text-gray-400">{t.noPortfolioYet}</div>
          ) : (
            <div className="space-y-5">
              {profile.portfolioCases.map((item) => {
                const images = item.media.map((m) => m.fileUrl);
                return (
                  <div key={item.id} className="rounded-xl border border-gray-100 p-4">
                    <div className="font-semibold text-gray-900">{item.title}</div>
                    <div className="mt-1.5 text-sm leading-6 text-gray-500">{item.description}</div>
                    {images.length > 0 ? (
                      <div className="mt-3 grid grid-cols-3 gap-2">
                        {images.map((url, i) => (
                          <button
                            key={url}
                            type="button"
                            onClick={() => openLightbox(images, i)}
                            className="relative overflow-hidden rounded-xl active:opacity-80 transition-opacity"
                          >
                            <img
                              src={url}
                              alt={`${item.title} ${i + 1}`}
                              className="h-24 w-full object-cover"
                            />
                            {i === 2 && images.length > 3 ? (
                              <div className="absolute inset-0 flex items-center justify-center bg-black/50 text-sm font-bold text-white">
                                +{images.length - 3}
                              </div>
                            ) : null}
                          </button>
                        ))}
                      </div>
                    ) : null}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* Lightbox */}
      {lightbox ? (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/95"
          onClick={() => setLightbox(null)}
        >
          {/* Close */}
          <button
            type="button"
            onClick={() => setLightbox(null)}
            className="absolute right-4 top-4 flex h-10 w-10 items-center justify-center rounded-full bg-white/10 text-xl text-white active:bg-white/20"
          >
            ✕
          </button>

          {/* Counter */}
          <div className="absolute top-4 left-1/2 -translate-x-1/2 rounded-full bg-white/10 px-3 py-1 text-xs font-semibold text-white">
            {lightbox.index + 1} / {lightbox.images.length}
          </div>

          {/* Prev */}
          {lightbox.index > 0 ? (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); setLightbox((prev) => prev ? { ...prev, index: prev.index - 1 } : null); }}
              className="absolute left-3 flex h-12 w-12 items-center justify-center rounded-full bg-white/10 text-2xl text-white active:bg-white/20"
            >
              ‹
            </button>
          ) : null}

          {/* Image */}
          <img
            src={lightbox.images[lightbox.index]}
            alt=""
            className="max-h-[85vh] max-w-[92vw] rounded-xl object-contain"
            onClick={(e) => e.stopPropagation()}
          />

          {/* Next */}
          {lightbox.index < lightbox.images.length - 1 ? (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); setLightbox((prev) => prev ? { ...prev, index: prev.index + 1 } : null); }}
              className="absolute right-3 flex h-12 w-12 items-center justify-center rounded-full bg-white/10 text-2xl text-white active:bg-white/20"
            >
              ›
            </button>
          ) : null}

          {/* Thumbnails */}
          {lightbox.images.length > 1 ? (
            <div className="absolute bottom-5 left-0 right-0 flex justify-center gap-1.5 px-4">
              {lightbox.images.map((_, i) => (
                <button
                  key={i}
                  type="button"
                  onClick={(e) => { e.stopPropagation(); setLightbox((prev) => prev ? { ...prev, index: i } : null); }}
                  className={`h-1.5 rounded-full transition-all ${i === lightbox.index ? "w-6 bg-white" : "w-1.5 bg-white/40"}`}
                />
              ))}
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
